import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

export type ChatMarkdownTone = 'panel' | 'bubbleAssistant';

const baseComponents: Components = {
    h1: ({ children }) => (
        <h1 className="text-lg font-semibold text-slate-100 mt-3 mb-2 first:mt-0">{children}</h1>
    ),
    h2: ({ children }) => (
        <h2 className="text-base font-semibold text-slate-100 mt-3 mb-2 first:mt-0">{children}</h2>
    ),
    h3: ({ children }) => (
        <h3 className="text-sm font-semibold text-slate-200 mt-2 mb-1.5 first:mt-0">{children}</h3>
    ),
    p: ({ children }) => (
        <p className="mb-2 last:mb-0 leading-relaxed text-slate-300/95">{children}</p>
    ),
    ul: ({ children }) => (
        <ul className="list-disc list-outside my-2 ml-4 space-y-1 text-slate-300/95">{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className="list-decimal list-outside my-2 ml-4 space-y-1 text-slate-300/95">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => (
        <strong className="font-semibold text-slate-100">{children}</strong>
    ),
    em: ({ children }) => <em className="italic text-slate-300/95">{children}</em>,
    a: ({ href, children }) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-400/90 underline decoration-cyan-500/50 hover:text-cyan-300"
        >
            {children}
        </a>
    ),
    code: ({ className, children, ...props }) => {
        const isBlock = Boolean(className && /^language-/.test(className));
        if (isBlock) {
            return (
                <code className={className} {...props}>
                    {children}
                </code>
            );
        }
        return (
            <code
                className="px-1 py-0.5 rounded bg-slate-800/90 text-emerald-300/90 text-[0.9em] font-chat-mono"
                {...props}
            >
                {children}
            </code>
        );
    },
    pre: ({ children }) => (
        <pre className="my-2 p-3 rounded-lg bg-slate-950/95 border border-slate-700/50 overflow-x-auto text-sm font-chat-mono leading-relaxed text-emerald-400/90">
            {children}
        </pre>
    ),
    table: ({ children }) => (
        <div className="chat-md-table-wrap my-3 overflow-x-auto rounded-lg border border-slate-700/50">
            <table className="w-full text-sm border-collapse">{children}</table>
        </div>
    ),
    thead: ({ children }) => <thead className="bg-slate-800/90 text-slate-200">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr className="border-b border-slate-700/40 last:border-b-0">{children}</tr>,
    th: ({ children }) => (
        <th className="px-2.5 py-2 text-left font-medium border-r border-slate-700/40 last:border-r-0">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="px-2.5 py-2 align-top border-r border-slate-700/40 last:border-r-0 text-slate-300/95">
            {children}
        </td>
    ),
    hr: () => <hr className="my-4 border-slate-600/60" />,
    blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-slate-500/70 pl-3 my-2 text-slate-400 italic">
            {children}
        </blockquote>
    ),
};

const toneWrapper: Record<ChatMarkdownTone, string> = {
    panel: '',
    bubbleAssistant:
        '[&_p]:text-cyan-900/85 [&_li]:text-cyan-900/85 [&_h1]:text-cyan-950 [&_h2]:text-cyan-950 [&_h3]:text-cyan-900 [&_strong]:text-cyan-950 [&_em]:text-cyan-800/90 [&_a]:text-cyan-700 [&_a]:decoration-cyan-600/40 [&_code]:bg-cyan-100/90 [&_code]:text-cyan-900 [&_td]:text-cyan-900/90 [&_th]:text-cyan-950 [&_thead]:bg-cyan-100/70 [&_tr]:border-cyan-200/50 [&_th]:border-cyan-200/50 [&_td]:border-cyan-200/50 [&_.chat-md-table-wrap]:border-cyan-200/80 [&_blockquote]:border-cyan-400/45 [&_blockquote]:text-cyan-800/85',
};

export interface ChatMarkdownBodyProps {
    source: string;
    tone?: ChatMarkdownTone;
    className?: string;
}

/**
 * Renders assistant markdown (GFM: tables, lists, task lists, strikethrough) with chat-friendly styling.
 */
export const ChatMarkdownBody: React.FC<ChatMarkdownBodyProps> = ({
    source,
    tone = 'panel',
    className = '',
}) => {
    if (!source.trim()) return null;
    return (
        <div
            className={`chat-markdown-body text-base leading-relaxed ${toneWrapper[tone]} ${className}`.trim()}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={baseComponents}>
                {source}
            </ReactMarkdown>
        </div>
    );
};
